import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { PermissionsService } from '../permissions/permissions.service';

/**
 * Memoized dynamic import for `route-permission-map` (and `MENU_CATALOG` behind it). Keeping this
 * import dynamic instead of static prevents the menu catalog from being pulled into the initial
 * bundle: `permissionGuard` is statically wired into `app.routes.ts`, so any static import here
 * would be reachable from the initial chunk even though the catalog is otherwise only reached
 * through the lazy menu page.
 */
let routeMapModule: Promise<typeof import('../permissions/route-permission-map')> | null = null;

function loadRouteMapModule(): Promise<typeof import('../permissions/route-permission-map')> {
  routeMapModule ??= import('../permissions/route-permission-map');
  return routeMapModule;
}

/**
 * Enforces the `Mobile.*` permission mapped to each `MENU_CATALOG` route (design §3). Routes
 * absent from `ROUTE_PERMISSION_MAP` fail open (D8) — they stay auth-only and this guard never
 * triggers a permission fetch for them. For mapped routes it awaits any in-flight/pending
 * permission load before deciding, so a deep-link or page refresh never gets a false redirect
 * against an empty grant set (design "Guard Awaits Pending Load").
 *
 * `errorKind() === 'cold'` (transport failure, nothing ever loaded) redirects to `/menu` instead
 * of `/not-authorized` — the feed state is undecidable, not a denial, and `/menu` already owns
 * the retry affordance (design ADR-6). Every other outcome (ready, noAccess, errorStale) is
 * decided purely by `hasPermission()`, since noAccess and a granted-but-stale/ready state with
 * no matching grant both correctly resolve to `/not-authorized`.
 *
 * `!isSettled()` after the await covers the same "undecidable" case as `errorKind() === 'cold'`
 * for a different cause: `clear()` (e.g. session expiry) landing while `ensureLoaded()` was in
 * flight resets the feed to `idle` and discards the pending response via its own generation
 * guard, so `errorKind()` reads `null` and `hasPermission()` reads an empty grant set — without
 * this check that combination would be misread as a genuine denial and race the interceptor's
 * own `/session-expired` navigation.
 */
export const permissionGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  const { ROUTE_PERMISSION_MAP, normalizePath } = await loadRouteMapModule();

  const permission = ROUTE_PERMISSION_MAP.get(normalizePath(state.url));
  if (permission === undefined) return true;

  await permissions.ensureLoaded();

  if (permissions.errorKind() === 'cold' || !permissions.isSettled()) return router.createUrlTree(['/menu']);

  return permissions.hasPermission(permission) ? true : router.createUrlTree(['/not-authorized']);
};
